'use client';

import { upperFirst } from 'lodash';
import { tss } from 'tss-react/mui';

import { useTheme } from '@mui/material/styles';

import {
  Collapse,
  CardHeader,
  List,
  Divider,
  ListSubheader,
  CircularProgress,
} from '@mui/material';

import EventIcon from '@mui/icons-material/Event';

import { Or2Card } from 'Components/Surface/Or2Card';

import { prettyDateString } from 'Utils/date/prettyDateStrings';
import { groupEventsByDate } from 'Utils/events/groupEventsByDate';

import type {
  GenericEventEventFragment as Event,
  GenericEventGovernanceVisitFieldsFragment as GovernanceVisit,
  GenericEventTrainingFragment as TrainingSession,
} from '@/gql/graphql';

import { Or2Avatar } from '@/components/Avatar/Or2Avatar';

import { EventListItem } from './EventListItem';
import { GovernanceVisitListItem } from './GovernanceVisitListItem';
import { TrainingSessionListItem } from './TrainingSessionListItem';

type GenericEvent = Event | TrainingSession | GovernanceVisit;

type EventWithStart = GenericEvent & { start: string };

const useStyles = tss.create(({ theme }) => {
  return {
    titleAvatar: {
      backgroundColor: theme.palette.primary.light,
    },
    // loading spinner classes to mitigate material classes in card header
    loadingSpinnerRoot: {
      display: 'block',
    },
    loadingSpinnerWrapper: {
      marginTop: 0,
      marginRight: 0,
      alignSelf: 'auto',
    },
    titleAvatarEmpty: {
      backgroundColor: theme.palette.error.main,
    },
    listSubheader: {
      lineHeight: '20px',
      color: theme.palette.primary.dark,
    },
    listSubheaderPadding: {
      paddingBottom: theme.spacing(1),
    },
    padding: {
      paddingTop: theme.spacing(2),
    },
  };
});

export interface GenericEventsCollectionFilters {
  limit?: number | undefined;
  reverse?: boolean | undefined;
}

interface Props {
  className?: string | undefined;
  events: GenericEvent[];
  title: React.ReactNode;
  emptyTitle: React.ReactNode;
  loading: boolean;
  showOwner: boolean;
  filters?: GenericEventsCollectionFilters;
}

export const GenericEventsCollection = ({
  className,
  events,
  title,
  emptyTitle,
  loading,
  showOwner = true,
  filters = { limit: 5, reverse: false },
}: Props) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();

  const { limit, reverse } = filters || {};

  const eventsWithStart = events.filter((event): event is EventWithStart =>
    Boolean(event.start),
  );

  const eventsGroupedByDate = groupEventsByDate(eventsWithStart);

  if (reverse) {
    eventsGroupedByDate.reverse();
  }

  const filteredEventsGroupedByDate = limit
    ? eventsGroupedByDate.slice(0, limit)
    : eventsGroupedByDate;

  const isLoadedEmpty = !loading && eventsGroupedByDate.length === 0;

  return (
    <Or2Card className={className}>
      <CardHeader
        classes={{ action: classes.loadingSpinnerWrapper }}
        avatar={
          <Or2Avatar
            className={cx(classes.titleAvatar, {
              [classes.titleAvatarEmpty]: isLoadedEmpty,
            })}
          >
            <EventIcon />
          </Or2Avatar>
        }
        action={
          loading ? (
            <CircularProgress
              classes={{ root: classes.loadingSpinnerRoot }}
              size={theme.spacing(3)}
              disableShrink
            />
          ) : null
        }
        title={isLoadedEmpty ? emptyTitle : title}
      />

      <Collapse in={events.length > 0} timeout="auto">
        <Divider />
        {filteredEventsGroupedByDate ? (
          <List>
            {filteredEventsGroupedByDate.map((eventGroup) => {
              const events = eventGroup?.events;

              const date = eventGroup.date;

              return (
                <div
                  key={date.toString()}
                  className={showOwner ? undefined : classes.padding}
                >
                  <ListSubheader
                    className={cx(classes.listSubheader, {
                      [classes.listSubheaderPadding]: showOwner,
                    })}
                  >
                    {upperFirst(prettyDateString(date))}
                  </ListSubheader>
                  {events.map((eventOrSession) => {
                    const id = eventOrSession?._id || '';

                    if (eventOrSession.__typename === 'Event') {
                      return (
                        <EventListItem
                          key={id}
                          event={eventOrSession}
                          showOwner={showOwner}
                        />
                      );
                    }

                    if (eventOrSession.__typename === 'GovernanceVisit') {
                      return (
                        <GovernanceVisitListItem
                          key={id}
                          visit={eventOrSession}
                          showOwner={showOwner}
                        />
                      );
                    }

                    return (
                      <TrainingSessionListItem
                        key={id}
                        session={eventOrSession}
                        showOwner={showOwner}
                      />
                    );
                  })}
                </div>
              );
            })}
          </List>
        ) : null}
      </Collapse>
    </Or2Card>
  );
};
